// Nivel e historial de crédito del cliente de una venta, a partir de su celda de cartera.
// Funciones puras: la ficha solo compone.
//
// El nivel que se muestra es el de CRÉDITO (bronce/plata/oro), derivado del SCI. No es el nivel 1/2
// de la cuenta PagoConFiado, que es otro dato y vive en su propia sección.
import type { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { CreditPortfolioEntry } from '../../customers/lib/creditPortfolio';
import { arrearsLabel, sciSourceNote, tierMissingNote } from '../../customers/lib/customersFormat';

/** Color del número de una tarjeta del historial, como el mock. */
export type CreditCardTone = 'neutral' | 'success' | 'danger';

export interface CreditHistoryCard {
    label: string;
    /** `null` cuando no hay cifra que dar: la tarjeta va en guion, nunca en cero. */
    value: string | null;
    tone: CreditCardTone;
}

/** La venta sin ficha de cliente no tiene a quién preguntarle: no es un cliente sin créditos. */
export const SALE_WITHOUT_CUSTOMER_NOTE =
    'Esta venta no quedó ligada a una ficha de cliente, así que no hay 360 ni expediente de crédito que leer.';

/** Las tres tarjetas del historial: créditos totales, liquidados y la mora del crédito vigente. */
export function creditHistoryCards(entry: CreditPortfolioEntry | null): CreditHistoryCard[] {
    return [
        {
            label: 'Créd. totales',
            value: entry ? String(entry.creditsCount) : null,
            tone: 'neutral',
        },
        {
            label: 'Liquidados',
            value: entry ? String(entry.settledCreditsCount) : null,
            tone: 'success',
        },
        {
            label: 'Días de mora',
            value: entry ? arrearsLabel(entry.arrearsDays, entry.hasActiveCredit) : null,
            tone: entry?.isInArrears ? 'danger' : 'neutral',
        },
    ];
}

export interface CreditTierFacts {
    /** El nivel crudo, tal como lo pide la píldora. `null` es ausencia de dato, no un nivel. */
    level: ClientLevelEnum | null;
    /** El porqué al pasar el cursor: de dónde salió el nivel, o por qué no lo hay. */
    note: string;
}

/** El nivel de crédito del cliente y de dónde salió. Quién lo pinta es la píldora. */
export function creditTierFacts(entry: CreditPortfolioEntry): CreditTierFacts {
    const level = entry.clientLevel;
    if (level === null) return { level: null, note: tierMissingNote(entry.creditsCount) };
    return { level, note: sciSourceNote(entry.sciSource) };
}
